import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../../Config/axiosInstance';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, MenuItem, Button, Grid, Typography, CircularProgress,
  Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select,
  Checkbox, FormControl, InputLabel
} from '@mui/material';
import { Save, PictureAsPdf, Assessment, TableChart } from '@mui/icons-material';
import './ExpensesReports.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import { saveAs } from "file-saver";
import logoImg from '../../../Images/logo_pmpc.png';

const ExpensesReportsActualCost = () => {
  const [filters, setFilters] = useState({
    month: (new Date().getMonth() + 1).toString().padStart(2, '0'),
    year: new Date().getFullYear(),
    expenseTypes: []
  });

  const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);

  const monthOptions = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  const fetchExpenseTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await axiosInstance.get('/expenses-type/');
      setExpenseTypeOptions(response.data);
      const allTypeIds = response.data.map(type => type.id);
      setFilters(prev => ({ ...prev, expenseTypes: allTypeIds }));
    } catch (err) {
      console.error('❌ [ERROR] Eroare la încărcarea tipurilor:', err);
      setError('Error loading the expense types.');
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchReport = useCallback(async () => {
    const processReportData = (rawData) => {
      if (!rawData || rawData.length === 0) {
        return { processedResults: [], grandTotalWithoutVat: 0, grandTotalWithVat: 0 };
      }

      const groupedByType = {};

      rawData.forEach(item => {
        const type = item.expense_type;
        if (!type) return;

        if (!groupedByType[type]) {
          groupedByType[type] = {
            items: [],
            target: item.target, // NU folosi || 0, păstrează valoarea chiar dacă e 0.00
            typeName: type
          };
        }
        groupedByType[type].items.push(item);
      });

      const processedResults = [];
      let grandTotalWithoutVat = 0;
      let grandTotalWithVat = 0;

      const sortedTypes = Object.keys(groupedByType).sort((a, b) => {
        const order = [
          'Cheltuieli chirii/unitari',
          'Cheltuieli combustibili',
          'Cheltuieli consumabile si secretariat',
          'Cheltuieli eclip, protectie+medicina munci',
          'Cheltuieli interne suplimentare',
          'Cheltuieli intretinere, reparati (fara auto)',
          'Cheltuieli materiale',
          'Cheltuieli parc auto'
        ];
        return order.indexOf(a) - order.indexOf(b);
      });

      sortedTypes.forEach(type => {
        const group = groupedByType[type];
        let typeTotalWithoutVat = 0;
        let typeTotalWithVat = 0;

        // Rândul 1: Header cu expense type și target
        processedResults.push({
          id: `header_${type}`,
          expense_type: type,
          target: group.target, // Păstrează valoarea originală
          project_code: group.items[0]?.project_code || '',
          remarks: group.items[0]?.remarks || '',
          resource: group.items[0]?.resource || '',
          month_report: filters.month,
          without_vat: '',
          with_vat: '',
          is_header: true,
          row_type: 'header'
        });

        // Rândurile detaliate (fără expense type)
        group.items.forEach((item, index) => {
          const withoutVat = parseFloat(item.without_vat) || 0;
          const withVat = parseFloat(item.with_vat) || 0;

          typeTotalWithoutVat += withoutVat;
          typeTotalWithVat += withVat;
          grandTotalWithoutVat += withoutVat;
          grandTotalWithVat += withVat;

          // Doar de la al doilea item începem cu rânduri noi
          if (index > 0) {
            processedResults.push({
              id: item.id || `detail_${type}_${index}`,
              expense_type: '',
              target: '',
              project_code: item.project_code || '',
              remarks: item.remarks || '',
              resource: item.resource || '',
              month_report: filters.month,
              without_vat: withoutVat,
              with_vat: withVat,
              is_detail: true,
              row_type: 'detail'
            });
          } else {
            // Pentru primul item, folosim datele de pe rândul header
            processedResults[processedResults.length - 1].without_vat = withoutVat;
            processedResults[processedResults.length - 1].with_vat = withVat;
          }
        });

        // Rândul TOTAL
        processedResults.push({
          id: `total_${type}`,
          expense_type: 'TOTAL',
          target: '', // GOL pentru coloana TARGET în rândul TOTAL
          project_code: '',
          remarks: '',
          resource: '',
          month_report: '',
          without_vat: typeTotalWithoutVat,
          with_vat: typeTotalWithVat,
          is_total: true,
          row_type: 'total'
        });
      });

      // Adaugă grand total
      if (processedResults.length > 0) {
        processedResults.push({
          id: 'grand_total',
          expense_type: 'GRAND TOTAL',
          target: '', // GOL pentru coloana TARGET în rândul GRAND TOTAL
          project_code: '',
          remarks: '',
          resource: '',
          month_report: '',
          without_vat: grandTotalWithoutVat,
          with_vat: grandTotalWithVat,
          is_grand_total: true,
          row_type: 'grand_total'
        });
      }

      return {
        processedResults,
        grandTotalWithoutVat,
        grandTotalWithVat
      };
    };
    setLoading(true);
    setError('');
    setReportData(null);
    setReportGenerated(false);

    try {
      const params = {
        month: filters.month,
        year: filters.year
      };

      if (filters.expenseTypes && filters.expenseTypes.length > 0) {
        params.expense_types = filters.expenseTypes.join(',');
      }

      const response = await axiosInstance.get('/reports-expenses-actual-cost/', { params });

      if (!response.data?.results || response.data.results.length === 0) {
        setReportData({
          results: [],
          summary: {
            grand_total_without_vat: 0,
            grand_total_with_vat: 0,
            total_records: 0
          },
          report_date: new Date().toISOString(),
          month: filters.month,
          year: filters.year,
          count: 0
        });
        setReportGenerated(true);
        return;
      }

      const processedData = processReportData(response.data.results);

      const reportWithStructure = {
        ...response.data,
        results: processedData.processedResults,
        summary: {
          grand_total_without_vat: processedData.grandTotalWithoutVat,
          grand_total_with_vat: processedData.grandTotalWithVat,
          total_records: response.data.count || response.data.results.length
        }
      };

      setReportData(reportWithStructure);
      setReportGenerated(true);
      setError('');

    } catch (err) {
      let errorMessage = 'Eroare la încărcarea raportului. ';

      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMessage += 'Parametri incorecți. Verifică filtrele.';
            break;
          case 404:
            errorMessage += 'Endpoint-ul API nu a fost găsit.';
            break;
          case 500:
            errorMessage += 'Eroare internă de server. Verifică logs-urile backend-ului.';
            break;
          default:
            errorMessage += `Status: ${err.response.status}`;
        }

        if (err.response.data?.error) {
          errorMessage += ` | ${err.response.data.error}`;
        }
      } else if (err.request) {
        errorMessage += 'Nu s-a primit răspuns de la server. Verifică conexiunea la rețea.';
      } else {
        errorMessage += err.message || 'Eroare necunoscută.';
      }

      setError(errorMessage);
      setReportData(null);
      setReportGenerated(false);
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year, filters.expenseTypes]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setReportGenerated(false);
  };

  const handleExpenseTypeChange = (event) => {
    const { value } = event.target;

    if (value.includes('All')) {
      if (filters.expenseTypes.length === expenseTypeOptions.length) {
        setFilters(prev => ({ ...prev, expenseTypes: [] }));
      } else {
        const allTypeIds = expenseTypeOptions.map(type => type.id);
        setFilters(prev => ({ ...prev, expenseTypes: allTypeIds }));
      }
    } else {
      const filteredValue = value.filter(item => item !== 'All');
      setFilters(prev => ({ ...prev, expenseTypes: filteredValue }));
    }

    setReportGenerated(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReport();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  }

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '' || isNaN(num)) return '';

    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return '';

    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const renderSelectedExpenseTypes = (selected) => {
    if (selected.length === 0) return 'None selected';
    if (selected.length === expenseTypeOptions.length) return 'All';
    return `${selected.length} selected`;
  };

  const reportRef = useRef();

  const exportPDF = async () => {
    if (!reportData?.results?.length) {
      alert("No data to export!");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const logo = "/logo_pmpc.png";
    const triangleWidth = 20;
    const totalWidth = pdfWidth * 0.25;
    const barWidth = totalWidth - triangleWidth;
    const barHeight = 1.2;
    const secondBarHeight = 2;

    const selectedMonthName = monthOptions.find(m => m.value === filters.month)?.label || "";
    const reportTitle = `Expenses Report - Actual Cost - ${selectedMonthName} ${filters.year}`;

    const totalPages = {};

    const drawHeader = (pageNumber) => {
      // Logo-ul pe toate paginile
      pdf.addImage(logo, "PNG", 8, 3, 28, 8);

      // Bara cu triunghi doar pe prima pagină
      if (pageNumber === 1) {
        const barTop = 15;
        const barBottom = barTop + barHeight;

        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        pdf.triangle(
          barWidth, barBottom,
          barWidth + triangleWidth, barBottom,
          barWidth, barTop,
          "F"
        );

        // Titlul raportului doar pe prima pagină
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(reportTitle, pdfWidth / 2, headerLongBarTop + 12, { align: "center" });
      }

      // Bara lungă albastră pe toate paginile
      const barTop = 15;
      const barBottom = barTop + barHeight;
      const overlap = 0.6;
      const headerLongBarTop = barBottom - overlap;

      pdf.setFillColor(23, 52, 88);
      pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

      // Linia de delimitare a header-ului pe toate paginile
      const headerBottomY = 40;
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(5, headerBottomY, pdfWidth - 5, headerBottomY);
    };

    const drawFooter = (pageNumber, pageCount) => {
      const bottomOffset = 12;
      const longBarTop = pdfHeight - bottomOffset;
      const verticalOverlapFooter = 0.6;

      // Desenează bara lungă de jos
      pdf.setFillColor(23, 52, 88);
      pdf.rect(
        0,
        longBarTop - verticalOverlapFooter + 0.1,
        pdfWidth,
        secondBarHeight,
        "F"
      );

      // Desenează bara scurtă cu triunghi
      const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
      const shortBarBottom = shortBarTop + barHeight;
      const barOverlapFooter = 1;
      pdf.rect(
        0,
        shortBarTop,
        barWidth + barOverlapFooter,
        barHeight,
        "F"
      );

      pdf.triangle(
        barWidth, shortBarTop,
        barWidth + triangleWidth, shortBarTop,
        barWidth, shortBarBottom,
        "F"
      );

      // Text footer
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
      const today = new Date();
      const currentDate = today.toLocaleDateString("ro-RO");
      pdf.text(`${currentDate}`, pdfWidth - 30, pdfHeight - 3);

      // Numărul paginii
      pdf.text(`Page ${pageNumber} of ${pageCount}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
    };

    // Funcție pentru formatarea numerelor
    const formatNumberForPDF = (num) => {
      if (num === null || num === undefined || num === '' || isNaN(num)) return '';
      const number = typeof num === 'string' ? parseFloat(num) : num;
      if (isNaN(number)) return '';
      return new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number);
    };

    // Header-ul tabelului - EXACT ca în UI
    const tableHeaders = [[
      { content: "EXPENSE TYPE", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "TARGET", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "PROJECT CODE", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "REMARKS", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "RESOURCE", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "MONTH REPORT", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "ACTUAL COST WITHOUT VAT", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
      { content: "ACTUAL COST WITH VAT", styles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' } },
    ]];

    const tableData = [];

    // Construim datele tabelului EXACT ca în UI
    reportData.results.forEach((row) => {
      const rowData = [];

      // EXPENSE TYPE
      let expenseTypeContent = row.expense_type || '';
      let expenseTypeStyles = {
        halign: 'left',
        valign: 'middle'
      };

      // TARGET
      let targetContent = '';
      let targetStyles = {
        halign: 'center',
        valign: 'middle'
      };

      // PROJECT CODE
      let projectCodeContent = row.project_code || '';
      let projectCodeStyles = {
        halign: 'center',
        valign: 'middle'
      };

      // REMARKS
      let remarksContent = row.remarks || '';
      let remarksStyles = {
        halign: 'center',
        valign: 'middle'
      };

      // RESOURCE
      let resourceContent = row.resource || '';
      let resourceStyles = {
        halign: 'center',
        valign: 'middle'
      };

      // MONTH REPORT
      let monthReportContent = row.month_report || '';
      let monthReportStyles = {
        halign: 'center',
        valign: 'middle'
      };

      // WITHOUT VAT
      let withoutVatContent = '';
      let withoutVatStyles = {
        halign: 'center',
        valign: 'middle',
        font: 'monospace'
      };

      // WITH VAT
      let withVatContent = '';
      let withVatStyles = {
        halign: 'center',
        valign: 'middle',
        font: 'monospace'
      };

      // Aplicăm stiluri specifice pentru fiecare tip de rând
      if (row.is_header) {
        // Header gri deschis pentru prima coloană, restul albe
        expenseTypeStyles.fillColor = [248, 248, 248]; // #f8f8f8
        expenseTypeStyles.fontStyle = 'bold';
        expenseTypeStyles.textColor = [0, 0, 0];

        // Celelalte coloane albe
        targetStyles.fillColor = [255, 255, 255];
        projectCodeStyles.fillColor = [255, 255, 255];
        remarksStyles.fillColor = [255, 255, 255];
        resourceStyles.fillColor = [255, 255, 255];
        monthReportStyles.fillColor = [255, 255, 255];
        withoutVatStyles.fillColor = [255, 255, 255];
        withVatStyles.fillColor = [255, 255, 255];

        targetContent = row.target !== null && row.target !== '' ? formatNumberForPDF(row.target) : '';
        withoutVatContent = row.without_vat !== null && row.without_vat !== '' ? formatNumberForPDF(row.without_vat) : '';
        withVatContent = row.with_vat !== null && row.with_vat !== '' ? formatNumberForPDF(row.with_vat) : '';

      } else if (row.is_detail) {
        // Detalii - totul alb
        expenseTypeStyles.fillColor = [255, 255, 255];
        targetStyles.fillColor = [255, 255, 255];
        projectCodeStyles.fillColor = [255, 255, 255];
        remarksStyles.fillColor = [255, 255, 255];
        resourceStyles.fillColor = [255, 255, 255];
        monthReportStyles.fillColor = [255, 255, 255];
        withoutVatStyles.fillColor = [255, 255, 255];
        withVatStyles.fillColor = [255, 255, 255];

        expenseTypeContent = ''; // Expense Type gol pentru detalii
        targetContent = '';
        withoutVatContent = row.without_vat !== null && row.without_vat !== '' ? formatNumberForPDF(row.without_vat) : '';
        withVatContent = row.with_vat !== null && row.with_vat !== '' ? formatNumberForPDF(row.with_vat) : '';

      } else if (row.is_total) {
        // TOTAL - ALBASTRU mai închis și mai distinct pentru PDF
        expenseTypeStyles.fillColor = [179, 229, 252]; // Albastru mai deschis #b3e5fc
        targetStyles.fillColor = [179, 229, 252];
        projectCodeStyles.fillColor = [179, 229, 252];
        remarksStyles.fillColor = [179, 229, 252];
        resourceStyles.fillColor = [179, 229, 252];
        monthReportStyles.fillColor = [179, 229, 252];
        withoutVatStyles.fillColor = [179, 229, 252];
        withVatStyles.fillColor = [179, 229, 252];

        expenseTypeStyles.textColor = [13, 71, 161]; // Albastru foarte închis #0d47a1
        targetStyles.textColor = [13, 71, 161];
        projectCodeStyles.textColor = [13, 71, 161];
        remarksStyles.textColor = [13, 71, 161];
        resourceStyles.textColor = [13, 71, 161];
        monthReportStyles.textColor = [13, 71, 161];
        withoutVatStyles.textColor = [13, 71, 161];
        withVatStyles.textColor = [13, 71, 161];

        expenseTypeStyles.fontStyle = 'bold';
        targetStyles.fontStyle = 'bold';
        projectCodeStyles.fontStyle = 'bold';
        remarksStyles.fontStyle = 'bold';
        resourceStyles.fontStyle = 'bold';
        monthReportStyles.fontStyle = 'bold';
        withoutVatStyles.fontStyle = 'bold';
        withVatStyles.fontStyle = 'bold';

        expenseTypeStyles.halign = 'left';
        targetContent = ''; // Target gol pentru TOTAL
        withoutVatContent = row.without_vat !== null && row.without_vat !== '' ? formatNumberForPDF(row.without_vat) : '';
        withVatContent = row.with_vat !== null && row.with_vat !== '' ? formatNumberForPDF(row.with_vat) : '';

      } else if (row.is_grand_total) {
        // GRAND TOTAL - VERDE mai intens și mai distinct pentru PDF
        expenseTypeStyles.fillColor = [200, 230, 201]; // Verde mai intens #c8e6c9
        targetStyles.fillColor = [200, 230, 201];
        projectCodeStyles.fillColor = [200, 230, 201];
        remarksStyles.fillColor = [200, 230, 201];
        resourceStyles.fillColor = [200, 230, 201];
        monthReportStyles.fillColor = [200, 230, 201];
        withoutVatStyles.fillColor = [200, 230, 201];
        withVatStyles.fillColor = [200, 230, 201];

        expenseTypeStyles.textColor = [27, 94, 32]; // Verde foarte închis #1b5e20
        targetStyles.textColor = [27, 94, 32];
        projectCodeStyles.textColor = [27, 94, 32];
        remarksStyles.textColor = [27, 94, 32];
        resourceStyles.textColor = [27, 94, 32];
        monthReportStyles.textColor = [27, 94, 32];
        withoutVatStyles.textColor = [27, 94, 32];
        withVatStyles.textColor = [27, 94, 32];

        expenseTypeStyles.fontStyle = 'bold';
        targetStyles.fontStyle = 'bold';
        projectCodeStyles.fontStyle = 'bold';
        remarksStyles.fontStyle = 'bold';
        resourceStyles.fontStyle = 'bold';
        monthReportStyles.fontStyle = 'bold';
        withoutVatStyles.fontStyle = 'bold';
        withVatStyles.fontStyle = 'bold';

        expenseTypeStyles.halign = 'left';
        targetContent = ''; // Target gol pentru GRAND TOTAL
        withoutVatContent = row.without_vat !== null && row.without_vat !== '' ? formatNumberForPDF(row.without_vat) : '';
        withVatContent = row.with_vat !== null && row.with_vat !== '' ? formatNumberForPDF(row.with_vat) : '';
      } else {
        // Rând normal
        targetContent = row.target !== null && row.target !== '' ? formatNumberForPDF(row.target) : '';
        withoutVatContent = row.without_vat !== null && row.without_vat !== '' ? formatNumberForPDF(row.without_vat) : '';
        withVatContent = row.with_vat !== null && row.with_vat !== '' ? formatNumberForPDF(row.with_vat) : '';
      }

      // Adăugăm datele în rowData
      rowData.push(
        { content: expenseTypeContent, styles: expenseTypeStyles },
        { content: targetContent, styles: targetStyles },
        { content: projectCodeContent, styles: projectCodeStyles },
        { content: remarksContent, styles: remarksStyles },
        { content: resourceContent, styles: resourceStyles },
        { content: monthReportContent, styles: monthReportStyles },
        { content: withoutVatContent, styles: withoutVatStyles },
        { content: withVatContent, styles: withVatStyles }
      );

      tableData.push(rowData);
    });

    // Convertim la formatul autoTable (array simplu pentru body)
    const simpleTableData = tableData.map(row =>
      row.map(cell => cell.content)
    );

    const tableOptions = {
      startY: 40,
      head: tableHeaders.map(headerRow =>
        headerRow.map(cell => cell.content)
      ),
      body: simpleTableData,
      margin: { top: 40, bottom: 30, left: 5, right: 5 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        lineColor: [192, 192, 192], // #c0c0c0
        lineWidth: 0.25,
        valign: "middle",
        textColor: [0, 0, 0]
      },

      headStyles: {
        fillColor: [25, 118, 210], // #1976d2
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineWidth: 0.25,
        lineColor: [192, 192, 192],
      },

      didParseCell: function (data) {
        // Aplicăm stilurile custom pentru header
        if (data.section === 'head') {
          const headerCell = tableHeaders[data.row.index][data.column.index];
          if (headerCell && headerCell.styles) {
            Object.assign(data.cell.styles, headerCell.styles);
          }
        }

        // Aplicăm stilurile custom pentru body
        if (data.section === 'body' && data.row.index < tableData.length) {
          const rowCell = tableData[data.row.index][data.column.index];
          if (rowCell && rowCell.styles) {
            Object.assign(data.cell.styles, rowCell.styles);

            // Dacă e font monospace pentru sume
            if (rowCell.styles.font === 'monospace') {
              data.cell.styles.font = 'courier';
            }
          }

          // Linii mai groase pentru TOTAL și GRAND TOTAL
          const row = reportData.results[data.row.index];
          if (row && (row.is_total || row.is_grand_total)) {
            data.cell.styles.lineWidth = 0.5;
            data.cell.styles.lineColor = [192, 192, 192];
          }
        }
      },

      columnStyles: {
        0: { cellWidth: 30 }, // Expense Type
        1: { cellWidth: 20 }, // Target
        2: { cellWidth: 25 }, // Project Code
        3: { cellWidth: 35 }, // Remarks
        4: { cellWidth: 20 }, // Resource
        5: { cellWidth: 20 }, // Month Report
        6: { cellWidth: 25 }, // Without VAT
        7: { cellWidth: 25 }  // With VAT
      },

      didDrawPage: function (data) {
        drawHeader(data.pageNumber);

        if (data.pageCount) {
          totalPages.value = data.pageCount;
          drawFooter(data.pageNumber, data.pageCount);
        }
      },

      pageBreak: { avoid: ['total', 'grand_total'] },
    };

    autoTable(pdf, tableOptions);

    const finalPageCount = totalPages.value || pdf.internal.getNumberOfPages();

    for (let pageNumber = 1; pageNumber <= finalPageCount; pageNumber++) {
      pdf.setPage(pageNumber);
      drawFooter(pageNumber, finalPageCount);
    }

    pdf.save(`ExpensesReportActualCost_${filters.month}_${filters.year}.pdf`);
  };

  // De asemenea, actualizează și funcția exportToExcel pentru culori mai distincte:
  const exportToExcel = async () => {
    const title = `Expenses Report - Actual Cost - ${monthOptions.find(m => m.value === filters.month)?.label} ${filters.year}`;

    if (!reportData?.results?.length) {
      alert("No data to export!");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Actual Cost Report');

    // Încărcare logo
    try {
      sheet.mergeCells('A1:A4');
      const logo = workbook.addImage({
        buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
        extension: 'png',
      });
      sheet.addImage(logo, { tl: { col: 0, row: 0 }, br: { col: 1, row: 4 } });
    } catch (error) {
      console.warn('Logo could not be loaded:', error);
    }

    // Titlu
    sheet.mergeCells('B1:H4');
    const titleCell = sheet.getCell('B1');
    titleCell.value = title;
    titleCell.font = { size: 14, bold: true };
    titleCell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true
    };

    // Spațiu
    sheet.insertRow(5, []);

    // Header-ul tabelului - EXACT ca în UI
    const headerRowNumber = 6;
    const headers = [
      "EXPENSE TYPE",
      "TARGET",
      "PROJECT CODE",
      "REMARKS",
      "RESOURCE",
      "MONTH REPORT",
      "ACTUAL COST WITHOUT VAT",
      "ACTUAL COST WITH VAT"
    ];

    const headerRow = sheet.getRow(headerRowNumber);
    headerRow.values = headers;
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.height = 28;

    // Stiluri header - EXACT ca în UI
    headerRow.eachCell((cell, colNumber) => {
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1976D2' } // Albastru #1976d2
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFC0C0C0' } },
        bottom: { style: 'thin', color: { argb: 'FFC0C0C0' } },
        left: { style: 'thin', color: { argb: 'FFC0C0C0' } },
        right: { style: 'thin', color: { argb: 'FFC0C0C0' } }
      };
    });

    // Adăugăm rândurile de date
    reportData.results.forEach((row, index) => {
      const dataRowNumber = headerRowNumber + 1 + index;

      // Determinăm conținutul pentru fiecare coloană
      const expenseType = row.is_detail ? '' : (row.expense_type || '');
      const target = (row.is_total || row.is_grand_total) ? '' : row.target;

      sheet.insertRow(dataRowNumber, [
        expenseType,
        target !== null && target !== '' ? target : '',
        row.project_code || '',
        row.remarks || '',
        row.resource || '',
        row.month_report || '',
        row.without_vat !== null && row.without_vat !== '' ? row.without_vat : '',
        row.with_vat !== null && row.with_vat !== '' ? row.with_vat : ''
      ]);

      const dataRow = sheet.getRow(dataRowNumber);
      dataRow.height = 28; // Înălțime uniformă ca în UI

      // Aplicăm border-uri pentru toate celulele
      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFC0C0C0' } },
          bottom: { style: 'thin', color: { argb: 'FFC0C0C0' } },
          left: { style: 'thin', color: { argb: 'FFC0C0C0' } },
          right: { style: 'thin', color: { argb: 'FFC0C0C0' } }
        };
      });

      // Aliniere specifică - EXACT ca în UI
      dataRow.getCell(1).alignment = {
        horizontal: 'left',
        vertical: 'middle',
        wrapText: true
      }; // Expense Type

      dataRow.getCell(2).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // Target

      dataRow.getCell(3).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // Project Code

      dataRow.getCell(4).alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true
      }; // Remarks

      dataRow.getCell(5).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // Resource

      dataRow.getCell(6).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // Month Report

      dataRow.getCell(7).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // Without VAT

      dataRow.getCell(8).alignment = {
        horizontal: 'center',
        vertical: 'middle'
      }; // With VAT

      // Formatare numerice
      if (row.target !== null && row.target !== '' && !isNaN(row.target) && !row.is_total && !row.is_grand_total) {
        dataRow.getCell(2).numFmt = '0.00';
      }

      if (row.without_vat !== null && row.without_vat !== '' && !isNaN(row.without_vat)) {
        dataRow.getCell(7).numFmt = '0.00';
      }

      if (row.with_vat !== null && row.with_vat !== '' && !isNaN(row.with_vat)) {
        dataRow.getCell(8).numFmt = '0.00';
      }

      // Stiluri pentru diferite tipuri de rânduri - EXACT ca în UI
      if (row.is_header) {
        // Header - EXACT ca în UI: doar prima coloană gri, restul albe
        dataRow.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8F8F8' } // #f8f8f8
        };
        dataRow.getCell(1).font = {
          bold: true,
          color: { argb: 'FF000000' } // Negru
        };

        // Restul coloanelor albe
        for (let i = 2; i <= 8; i++) {
          dataRow.getCell(i).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' } // Alb
          };
        }

        // Font monospace pentru sume
        if (row.without_vat !== null && row.without_vat !== '') {
          dataRow.getCell(7).font = { name: 'Courier New' };
        }
        if (row.with_vat !== null && row.with_vat !== '') {
          dataRow.getCell(8).font = { name: 'Courier New' };
        }

      } else if (row.is_detail) {
        // Detalii - totul alb
        dataRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' } // Alb
          };
          cell.font = {
            color: { argb: 'FF333333' } // #333
          };
        });

        // Font monospace pentru sume
        if (row.without_vat !== null && row.without_vat !== '') {
          dataRow.getCell(7).font = { name: 'Courier New', color: { argb: 'FF333333' } };
        }
        if (row.with_vat !== null && row.with_vat !== '') {
          dataRow.getCell(8).font = { name: 'Courier New', color: { argb: 'FF333333' } };
        }

      } else if (row.is_total) {
        // TOTAL - ALBASTRU mai distinct pentru Excel
        dataRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFB3E5FC' } // Albastru deschis #b3e5fc
          };
          cell.font = {
            bold: true,
            color: { argb: 'FF0D47A1' } // Albastru foarte închis #0d47a1
          };
        });

        // Font monospace pentru sume
        if (row.without_vat !== null && row.without_vat !== '') {
          dataRow.getCell(7).font = {
            name: 'Courier New',
            bold: true,
            color: { argb: 'FF0D47A1' }
          };
        }
        if (row.with_vat !== null && row.with_vat !== '') {
          dataRow.getCell(8).font = {
            name: 'Courier New',
            bold: true,
            color: { argb: 'FF0D47A1' }
          };
        }

        // Border mai gros dedesubt
        dataRow.eachCell(cell => {
          cell.border.bottom = { style: 'medium', color: { argb: 'FFC0C0C0' } };
        });

      } else if (row.is_grand_total) {
        // GRAND TOTAL - VERDE mai distinct pentru Excel
        dataRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFC8E6C9' } // Verde mai intens #c8e6c9
          };
          cell.font = {
            bold: true,
            color: { argb: 'FF1B5E20' } // Verde închis #1b5e20
          };
        });

        // Font monospace pentru sume
        if (row.without_vat !== null && row.without_vat !== '') {
          dataRow.getCell(7).font = {
            name: 'Courier New',
            bold: true,
            color: { argb: 'FF1B5E20' }
          };
        }
        if (row.with_vat !== null && row.with_vat !== '') {
          dataRow.getCell(8).font = {
            name: 'Courier New',
            bold: true,
            color: { argb: 'FF1B5E20' }
          };
        }

        // Border mai gros dedesubt
        dataRow.eachCell(cell => {
          cell.border.bottom = { style: 'medium', color: { argb: 'FFC0C0C0' } };
        });
      }
    });

    // Ajustăm lățimile coloanelor
    sheet.columns.forEach((column, columnIndex) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        let cellValue = cell.value?.toString() || '';
        if (cell.numFmt === '0.00' && !isNaN(cell.value)) {
          cellValue = parseFloat(cell.value || 0).toFixed(2);
        }
        maxLength = Math.max(maxLength, cellValue.length);
      });

      const columnWidths = [30, 15, 20, 35, 20, 15, 25, 25];
      column.width = columnWidths[columnIndex] || 15;
    });

    // Adăugăm footer - FIX AICI: elimin variabila nefolosită
    sheet.insertRow(sheet.rowCount + 2); // Inserează un rând gol
    const today = new Date();
    const footerText = `Generated on ${today.toLocaleDateString('ro-RO')} | ${monthOptions.find(m => m.value === filters.month)?.label} ${filters.year} | Expense Types: ${filters.expenseTypes.length === expenseTypeOptions.length ? 'All' : `${filters.expenseTypes.length} selected`}`;

    sheet.mergeCells(`A${sheet.rowCount}:H${sheet.rowCount}`);
    const footerCell = sheet.getCell(`A${sheet.rowCount}`);
    footerCell.value = footerText;
    footerCell.font = { size: 9, color: { argb: 'FF666666' } };
    footerCell.alignment = { horizontal: 'left' };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `ExpensesReportActualCost_${filters.month}_${filters.year}.xlsx`);
  };

  return (
    <Container maxWidth={false} disableGutters className="exp-reports expenses-reports">
      <Container maxWidth={false} disableGutters className="expense-container">
        <Typography variant="h4" gutterBottom marginTop="20px">
          Expenses Report - Actual Cost
        </Typography>

        <Paper className="filters-paper">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={9}>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                  <TextField
                    select
                    label="Month"
                    value={filters.month}
                    onChange={(e) => handleFilterChange('month', e.target.value)}
                    required
                    size="small"
                    className="month-select"
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        color: 'red'
                      }
                    }}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Year"
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    required
                    size="small"
                    className="year-select"
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        color: 'red'
                      }
                    }}
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>

                  <FormControl size="small" className="expense-type-form-control" sx={{ minWidth: 250 }}>
                    <InputLabel id="expense-type-label">Expense Type</InputLabel>
                    <Select
                      labelId="expense-type-label"
                      multiple
                      value={filters.expenseTypes}
                      onChange={handleExpenseTypeChange}
                      input={<OutlinedInput label="Expense Type" />}
                      renderValue={renderSelectedExpenseTypes}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            width: 250,
                            '& .MuiMenuItem-root': {
                              fontSize: '0.75rem',
                              paddingTop: 0.25,
                              paddingBottom: 0.25,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            },
                            '& .MuiCheckbox-root': {
                              padding: '2px',
                              marginRight: '4px',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="All">
                        <Checkbox
                          size="small"
                          checked={filters.expenseTypes.length === expenseTypeOptions.length}
                          sx={{ padding: '2px', marginRight: '4px' }}
                        />
                        <ListItemText primary="ALL" />
                      </MenuItem>
                      {expenseTypeOptions.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          <Checkbox
                            size="small"
                            checked={filters.expenseTypes.includes(type.id)}
                            sx={{ padding: '2px', marginRight: '4px' }}
                          />
                          <ListItemText primary={type.type || type.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                    className="generate-button"
                    startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                    disabled={loadingTypes || loading || expenseTypeOptions.length === 0}
                  >
                    Generate Report
                  </Button>
                </Box>
              </Grid>

              {/* MODIFICARE AICI: Butonul Save As apare doar când reportGenerated este true */}
              {reportGenerated && reportData?.results?.length > 0 && (
                <Grid item xs={12} md={3}>
                  <Grid container spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} wrap="wrap">
                    <Grid item>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleClick}
                        className="save-button"
                        startIcon={<Save />}
                        sx={{
                          backgroundColor: '#4caf50',
                          '&:hover': {
                            backgroundColor: '#388e3c',
                          }
                        }}
                      >
                        Save As
                      </Button>
                    </Grid>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                      <MenuItem
                        onClick={() => { handleClose(); exportPDF(); }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            '& .MuiListItemIcon-root': {
                              color: '#b71c1c',
                            }
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: '#d32f2f' }}>
                          <PictureAsPdf fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Save as PDF" />
                      </MenuItem>

                      <MenuItem
                        onClick={() => { handleClose(); exportToExcel(); }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(27, 94, 32, 0.08)',
                            '& .MuiListItemIcon-root': {
                              color: '#0d3c12',
                            }
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: '#1b5e20' }}>
                          <TableChart fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Save as Excel" />
                      </MenuItem>
                    </Menu>
                  </Grid>
                </Grid>
              )}
              {/* SFÂRȘIT MODIFICARE */}

            </Grid>
          </form>
        </Paper>

        {error && (
          <Alert severity="error" className="error-alert">
            {error}
          </Alert>
        )}

        <div ref={reportRef}>
          {reportGenerated && reportData?.results && reportData.results.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                marginTop: '20px',
                border: '1px solid #c0c0c0',
                borderRadius: '2px',
                boxShadow: 'none'
              }}
            >
              <Table
                className="report-table"
                size="small"
                sx={{
                  '& .MuiTableCell-root': {
                    border: '1px solid #c0c0c0',
                    padding: '4px 6px',
                    fontSize: '0.75rem',
                    lineHeight: '1.2'
                  },
                  borderCollapse: 'collapse'
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#1a237e', // Albastru închis pentru întregul header
                      '& th': {
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.875rem', // MODIFICARE: cresc dimensiunea fontului de la 0.75rem la 0.875rem
                        color: '#fff', // Text alb pentru contrast
                        padding: '12px 6px', // MODIFICARE: cresc padding-ul vertical
                        border: '1px solid #283593', // Albastru mai închis pentru border
                        borderBottom: '2px solid #000',
                        height: '48px', // MODIFICARE: cresc înălțimea pentru font mai mare
                        verticalAlign: 'middle', // MODIFICARE: aliniere verticală pe mijloc
                        fontFamily: 'Roboto, Arial, sans-serif', // Font mai modern și clar
                        letterSpacing: '0.3px' // MODIFICARE: adaug un mic spațiu între litere pentru lizibilitate
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului (bold este 700, 900 este extra bold)
                        color: '#fff',
                        minWidth: '150px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font specifică pentru prima coloană
                      }}
                    >
                      EXPENSE TYPE
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '80px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      TARGET
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '120px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      PROJECT CODE
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '150px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      REMARKS
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '100px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      RESOURCE
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '100px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      MONTH REPORT
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '130px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      ACTUAL COST WITHOUT VAT
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: '#1976d2',
                        fontWeight: '900', // MODIFICARE: cresc grosimea fontului
                        color: '#fff',
                        minWidth: '130px',
                        fontSize: '0.875rem' // MODIFICARE: dimensiune font
                      }}
                    >
                      ACTUAL COST WITH VAT
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.results.map((row, index) => {
                    const isHeader = row.is_header;
                    const isDetail = row.is_detail;
                    const isTotal = row.is_total;
                    const isGrandTotal = row.is_grand_total;

                    return (
                      <TableRow
                        key={row.id || index}
                        sx={{
                          backgroundColor:
                            isHeader ? '#f8f8f8' : // Background gri deschis pentru header-uri
                              isTotal ? '#e3f2fd' : // ALBASTRU DESCHIS pentru TOTAL (toată linia)
                                isGrandTotal ? '#e8f4e8' : // Background verde deschis pentru grand total
                                  'white',
                          height: '32px', // MODIFICARE: cresc înălțimea rândurilor
                          // Linie mai groasă sub TOTAL și GRAND TOTAL
                          borderBottom: isTotal || isGrandTotal ? '2px solid #c0c0c0' : '1px solid #c0c0c0'
                        }}
                      >

                        {/* EXPENSE TYPE - cu diferite stiluri pentru diferite tipuri de rânduri */}
                        <TableCell
                          sx={{
                            color: isHeader ? '#000' :
                              isTotal ? '#1976d2' : // Albastru pentru TOTAL
                                isGrandTotal ? '#1b5e20' : // Verde pentru GRAND TOTAL
                                  'inherit',
                            backgroundColor:
                              isHeader ? '#f8f8f8' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana Expense Type în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : // VERDE pentru coloana Expense Type în rândul GRAND TOTAL
                                    'inherit',
                            // Pentru rândurile de detalii (care nu sunt primul din grup), lăsăm gol
                            fontStyle: isDetail ? 'normal' : 'inherit',
                            // Pentru TOTAL și GRAND TOTAL, textul e mai vizibil și centrat
                            textAlign: isTotal || isGrandTotal ? 'left' : 'left',
                            // Font mai vizibil pentru TOTAL și GRAND TOTAL
                            fontSize: isTotal || isGrandTotal ? '0.8rem' : '0.75rem',
                            fontWeight: isTotal || isGrandTotal ? 'bold' : 'normal',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '150px' // MODIFICARE: lățime minimă consistentă
                          }}
                        >
                          {/* Pentru detalii lăsăm gol, altfel afișăm */}
                          {isDetail ? '' : row.expense_type}
                        </TableCell>

                        {/* TARGET - Pentru TOTAL și GRAND TOTAL, această coloană va fi goală */}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: isTotal || isGrandTotal ? 'bold' : 'normal',
                            color: isTotal ? '#1976d2' : isGrandTotal ? '#1b5e20' : '#000',
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana TARGET în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            fontFamily: (isTotal || isGrandTotal) ? 'monospace' : 'inherit',
                            // Pentru TOTAL și GRAND TOTAL, textul e mai vizibil
                            fontSize: isTotal || isGrandTotal ? '0.8rem' : '0.75rem',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '80px'
                          }}
                        >
                          {/* Pentru TOTAL și GRAND TOTAL, Target este gol, pentru restul afișăm formatNumber */}
                          {(isTotal || isGrandTotal) ? '' :
                            (row.target !== null && row.target !== '' ? formatNumber(row.target) : '')}
                        </TableCell>

                        {/* PROJECT CODE - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana PROJECT CODE în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            color: isTotal ? '#1976d2' : isDetail && row.project_code ? '#333' : 'inherit',
                            fontFamily: isDetail ? 'inherit' : 'inherit',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '120px'
                          }}
                        >
                          {row.project_code}
                        </TableCell>

                        {/* REMARKS - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana REMARKS în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            color: isTotal ? '#1976d2' : isDetail ? '#333' : 'inherit',
                            fontStyle: isDetail ? 'normal' : 'inherit',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '150px'
                          }}
                        >
                          {row.remarks}
                        </TableCell>

                        {/* RESOURCE - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana RESOURCE în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            color: isTotal ? '#1976d2' : isDetail && row.resource ? '#333' : 'inherit',
                            fontFamily: isDetail ? 'inherit' : 'inherit',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '100px'
                          }}
                        >
                          {row.resource}
                        </TableCell>

                        {/* MONTH REPORT - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana MONTH REPORT în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            color: isTotal ? '#1976d2' : isDetail ? '#333' : 'inherit',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '100px'
                          }}
                        >
                          {row.month_report}
                        </TableCell>

                        {/* ACTUAL COST WITHOUT VAT - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: isTotal || isGrandTotal ? 'bold' : 'normal',
                            color: isTotal ? '#1976d2' : isGrandTotal ? '#1b5e20' : '#000',
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana WITHOUT VAT în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            fontFamily: 'monospace',
                            fontSize: isTotal || isGrandTotal ? '0.8rem' : '0.75rem',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '130px'
                          }}
                        >
                          {row.without_vat !== null && row.without_vat !== '' ? formatNumber(row.without_vat) : ''}
                        </TableCell>

                        {/* ACTUAL COST WITH VAT - Pentru TOTAL, această coloană va fi albastră */}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: isTotal || isGrandTotal ? 'bold' : 'normal',
                            color: isTotal ? '#1976d2' : isGrandTotal ? '#1b5e20' : '#000',
                            backgroundColor:
                              isHeader ? '#ffff' :
                                isTotal ? '#e3f2fd' : // ALBASTRU pentru coloana WITH VAT în rândul TOTAL
                                  isGrandTotal ? '#e8f4e8' : 'inherit',
                            fontFamily: 'monospace',
                            fontSize: isTotal || isGrandTotal ? '0.8rem' : '0.75rem',
                            padding: '6px 8px', // MODIFICARE: cresc padding-ul
                            minWidth: '130px'
                          }}
                        >
                          {row.with_vat !== null && row.with_vat !== '' ? formatNumber(row.with_vat) : ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {reportGenerated && reportData?.results?.length === 0 && !loading && (
            <Alert severity="info" className="info-alert" sx={{ mt: 3, fontSize: '0.875rem' }}>
              There is no data in the database for the selected filters ({monthOptions.find(m => m.value === filters.month)?.label} {filters.year}).
            </Alert>
          )}

          {reportGenerated && reportData && reportData.results.length > 0 && (
            <Box className="report-footer" sx={{
              mt: 2,
              pt: 1,
              borderTop: '1px solid #e0e0e0',
              fontSize: '0.75rem',
              color: '#666'
            }}>
              <Typography variant="caption">
                Generated on {reportData.report_date ? new Date(reportData.report_date).toLocaleString('ro-RO') : new Date().toLocaleString('ro-RO')} |{' '}
                {monthOptions.find(m => m.value === filters.month)?.label} {filters.year} |{' '}
                Expense Types: {filters.expenseTypes.length === expenseTypeOptions.length ? 'All' : `${filters.expenseTypes.length} selected`}
              </Typography>
            </Box>
          )}
        </div>
      </Container>
    </Container>
  );
};

export default ExpensesReportsActualCost;